import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Drummond 2026 palette (CLAUDE.md). Series colours: fund navy, objective teal, peers amber.
export const C = {
  navy: '#012341', teal: '#337B91', slate: '#5E819C', tealDark: '#12706C', sage: '#A6C6C1',
  mauve: '#AA8090', lightBlue: '#BBCBD7', grey: '#3F4A59', greyMid: '#7F7F7F',
  warning: '#FBAE40', danger: '#F26B43', success: '#91CF9B', info: '#599BE3',
};
export const SERIES_COLOUR = {
  'Dynamic Plus Fund': C.navy, Objective: C.teal, 'FE AMI Flexible Peers': C.warning,
  'Global Equities': C.tealDark, 'Global Bonds': C.mauve, Cash: C.lightBlue,
};
// Asset-class colours by position in the backend's class order (Aus Eq, Intl Eq, Infra,
// Real Estate, Commodities, Gov Bonds, Corp Debt, Global Alpha, Cash): neighbours in the
// stack get contrasting hues (mauve between the teals and slate), cash stays amber.
export const AC_COLOURS = [C.navy, C.teal, C.tealDark, C.mauve, C.sage, C.slate, C.lightBlue, C.greyMid, C.warning, C.danger];

// Recharts 3.4.1's stackOffset="sign" is broken: offsetSign advances the running total
// by the series' raw value instead of the cumulative, so each bar sits on the previous
// bar's own height (Infrastructure drawn on International Equities' height, not on the
// top of the stack). Stack by hand instead: positives up from 0 and negatives down from
// 0, in key order, as [base, top] ranges, which Recharts uses verbatim for a stackId.
export const signStack = (rows, keys) => rows.map((r) => {
  const o = { ...r };
  let pos = 0;
  let neg = 0;
  keys.forEach((k) => {
    const v = isNum(r[k]) ? r[k] : 0;
    if (v >= 0) { o[k] = [pos, pos + v]; pos += v; } else { o[k] = [neg, neg + v]; neg += v; }
  });
  return o;
});
// Tooltip value for a hand-stacked series: the segment's own size, not its [base, top].
export const rangeVal = (v) => (Array.isArray(v) ? v[1] - v[0] : v);
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
export const pct = (v, dp = 1, signed = false) => {
  if (!isNum(v)) return null;
  const s = (v * 100).toFixed(dp) + '%';
  return signed && v > 0 ? '+' + s : s;
};
export const num = (v, dp = 0) => (isNum(v) ? v.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : null);
export const money = (v) => (isNum(v) ? 'A$' + (v / 1e6).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'm' : null);
export const Dash = () => <span className="pf-dash">–</span>;
export const V = ({ v, dp = 1, signed = false, kind = 'pct' }) => {
  const s = kind === 'pct' ? pct(v, dp, signed) : kind === 'money' ? money(v) : num(v, dp);
  return s === null ? <Dash /> : <>{s}</>;
};
export const signCls = (v) => (!isNum(v) || Math.abs(v) < 0.00005 ? '' : v > 0 ? 'pf-pos' : 'pf-neg');
export const tiltCls = (v, tol = 0.0005) => (!isNum(v) || Math.abs(v) < tol ? '' : v > 0 ? 'pf-tilt-over' : 'pf-tilt-under');
export const round10 = (v) => (isNum(v) ? Math.round(v / 10) * 10 : null);

export function useFetch(path, params, enabled = true) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get(path, { params: params || {} })
      .then((r) => setState({ data: r.data, error: null, loading: false }))
      .catch((e) => setState({ data: null, error: e.response?.data?.detail || e.message, loading: false }));
  }, [path, key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export const Status = ({ loading, error }) => (
  <>
    {loading && <div className="pf-muted">Loading…</div>}
    {error && <div className="pf-alert">{String(error)}</div>}
  </>
);

export const Tile = ({ label, value, sub, breach }) => (
  <div className={`pf-tile${breach ? ' pf-tile-breach' : ''}`}>
    <div className="pf-tile-label">{label}</div>
    <div className="pf-tile-value">{value ?? '–'}</div>
    {sub && <div className="pf-tile-sub">{sub}</div>}
  </div>
);
